import React, { useCallback, useEffect, useState } from 'react';
import { useAdminGuard } from '../AdminOnlyGuard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SmallSecondaryButton, SmallStandardButton } from '@/styles/StandardButtons';
import { checkLimits, DEFAULT_DAILY_LIMIT_USD, MIN_DAILY_LIMIT_USD, MIN_MONTHLY_CAP_USD } from '@/lib/ai-limits';
import { clearUserBudget, fetchCompanyUsage, formatUsd, saveUserBudget, type CompanyUsage, type TeamUsageUser } from './aiUsageApi';

type Draft = { daily: string; monthly: string };

/**
 * Settings > Usage > "Team AI usage" (company admins): every teammate's estimated consumption this month, the Daily limit (default $10, at least $1) and the
 * optional Monthly cap (empty = no cap, otherwise at least $25) the admin sets for each of them, and a read-only table of everyone's usage this month (User, %, Daily limit, Monthly cap, Used this month). The figures come from ONE request (GET
 * /api/ai-usage/company, admins only: the server refuses anyone else) -- the same data the editable rows above use -- so the two can never disagree. No provider
 * cost, customer amount or token detail is shown here.
 * All figures are estimates: no payment is processed.
 */
export function TeamAiUsageSection() {
  const canApply = useAdminGuard();
  const [data, setData] = useState<CompanyUsage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [rowMessage, setRowMessage] = useState<Record<string, { ok: boolean; text: string }>>({});

  const apply = useCallback((next: CompanyUsage) => {
    setData(next);
    if (next.available) {
      setDrafts(Object.fromEntries(next.users.map((u) => [u.user_id, { daily: String(u.daily_limit_usd), monthly: u.monthly_cap_usd === null ? '' : String(u.monthly_cap_usd) }])));
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      apply(await fetchCompanyUsage());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The team usage could not be loaded');
    } finally {
      setLoading(false);
    }
  }, [apply]);
  useEffect(() => { void load(); }, [load]);

  const setDraft = (userId: string, patch: Partial<Draft>) => setDrafts((d) => ({ ...d, [userId]: { ...d[userId], ...patch } }));
  const note = (userId: string, ok: boolean, text: string) => setRowMessage((m) => ({ ...m, [userId]: { ok, text } }));

  const save = async (user: TeamUsageUser) => {
    if (!canApply()) return;
    const draft = drafts[user.user_id];
    const check = checkLimits(draft.daily, draft.monthly);
    if (!check.ok) { note(user.user_id, false, check.message); return; }
    setBusy(user.user_id);
    try {
      apply(await saveUserBudget(user.user_id, { monthly_cap_usd: check.monthly, daily_limit_usd: check.daily }));
      note(user.user_id, true, 'Saved.');
    } catch (e) {
      note(user.user_id, false, e instanceof Error ? e.message : 'The limits could not be saved');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (user: TeamUsageUser) => {
    if (!canApply()) return;
    setBusy(user.user_id);
    try {
      apply(await clearUserBudget(user.user_id));
      note(user.user_id, true, `Back to the defaults ($${DEFAULT_DAILY_LIMIT_USD} a day, no monthly cap).`);
    } catch (e) {
      note(user.user_id, false, e instanceof Error ? e.message : 'The limits could not be removed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
    <Card className="max-w-4xl" data-testid="team-ai-usage">
      <CardHeader><CardTitle className="font-heading-text">Team Usage Limits</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        {loading && <p className="text-sm text-gray-500">Loading team usage...</p>}
        {!loading && error && (
          <div className="space-y-2">
            <div className="error-message" role="alert">{error}</div>
            <SmallSecondaryButton type="button" onClick={() => void load()}>Retry</SmallSecondaryButton>
          </div>
        )}
        {!loading && !error && data && !data.available && <p className="text-sm text-gray-500">{data.message}</p>}
        {!loading && !error && data && data.available && (
          <>
            <div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="team-usage-limits">
                  <thead>
                    <tr className="text-xs text-gray-500 text-left">
                      <th className="pb-1 font-normal">Person</th>
                      <th className="pb-1 font-normal pl-4">Daily limit (USD)</th>
                      <th className="pb-1 font-normal pl-2">Monthly cap (USD)</th>
                      <th className="pb-1 font-normal pl-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map((u) => (
                      <tr key={u.user_id} className="border-t border-gray-100 align-top">
                        <td className="py-2 pr-2"><span className="font-medium">{u.name}</span> <span className="text-xs text-gray-500">{u.role}</span></td>
                        <td className="py-2 pl-4">
                          <Input aria-label={`Daily limit for ${u.name}`} type="number" min={MIN_DAILY_LIMIT_USD} step="0.01" inputMode="decimal" className="h-8 w-28" value={drafts[u.user_id]?.daily ?? ''} placeholder={String(DEFAULT_DAILY_LIMIT_USD)} onChange={(e) => setDraft(u.user_id, { daily: e.target.value })} />
                        </td>
                        <td className="py-2 pl-2">
                          <Input aria-label={`Monthly cap for ${u.name}`} type="number" min={MIN_MONTHLY_CAP_USD} step="0.01" inputMode="decimal" className="h-8 w-28" value={drafts[u.user_id]?.monthly ?? ''} placeholder="No cap" onChange={(e) => setDraft(u.user_id, { monthly: e.target.value })} />
                        </td>
                        <td className="py-2 pl-3 whitespace-nowrap">
                          <div className="flex gap-2">
                            <SmallStandardButton type="button" customVariant="blue" disabled={busy === u.user_id} onClick={() => void save(u)}>Save</SmallStandardButton>
                            <SmallStandardButton type="button" customVariant="white" disabled={busy === u.user_id || !u.has_custom_limits} title={u.has_custom_limits ? undefined : 'No limits are configured for this person'} onClick={() => void remove(u)}>Remove</SmallStandardButton>
                          </div>
                          {rowMessage[u.user_id] && <p className={`mt-1 text-xs ${rowMessage[u.user_id].ok ? 'text-green-700' : 'text-red-700'}`} role={rowMessage[u.user_id].ok ? 'status' : 'alert'}>{rowMessage[u.user_id].text}</p>}
                        </td>
                      </tr>
                    ))}
                    {data.users.length === 0 && <tr><td colSpan={4} className="py-2 text-gray-500">No users.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>

    {/* its own box: the consumption of everyone, apart from the limits */}
    {!loading && !error && data && data.available && (
      <Card className="max-w-4xl" data-testid="usage-per-user">
        <CardHeader><CardTitle className="font-heading-text">Usage per user</CardTitle></CardHeader>
        <CardContent>
            <div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="team-usage-by-user">
                  <thead>
                    <tr className="text-xs text-gray-500 text-right">
                      <th className="pb-1 font-normal text-left">User</th><th className="pb-1 font-normal">%</th><th className="pb-1 font-normal">Used Today</th><th className="pb-1 font-normal">Used this month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map((u) => (
                      <tr key={u.user_id} className="border-t border-gray-100 text-right tabular-nums">
                        <td className="py-1.5 text-left"><span className="font-medium">{u.name}</span> <span className="text-xs text-gray-500">{u.role}</span></td>
                        <td>{u.month_percent === null ? '—' : `${u.month_percent}%`}</td>
                        <td>{formatUsd(u.today_weighted_usd)}</td>
                        <td>{formatUsd(u.weighted_usd)}</td>
                      </tr>
                    ))}
                    {data.users.length === 0 && <tr><td colSpan={4} className="py-2 text-gray-500 text-left">No users.</td></tr>}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-gray-500">Estimated consumption. Reaching the daily limit or the monthly cap stops new AI requests for that user.</p>
            </div>
        </CardContent>
      </Card>
    )}
    </>
  );
}
